import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../common';
import { calculateM4Range, calculateN1Range, getDefaultMinStartingVoltage, getNw, getStartingVoltageMin, isNullOrUndefined, mapToSliderInputText } from '../../../services';
import { LoadType, StartingType } from '../../../enums';
import { CommonData, MesRequest, Range } from '../../../models';
import { setAcText, setAwText, setMesRequest, setStartingVoltageText, toggleLoading } from '../../../store';
import { useHook } from '../../Hooks';

export const useStartingConstraint = () => {
    const dispatch = useAppDispatch();
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const updateFilters = useAppSelector(state => state.motor.updateFilters);
    const isStartingTypeDol = mesRequest.commonData.startingType === StartingType.DOL;
    const defaultStartingVoltageRange = { min: getDefaultMinStartingVoltage(isStartingTypeDol), max: 100, step: 1 };
    const [dolStartingVoltageRange, setDolStartingVoltageRange] = useState<Range>(defaultStartingVoltageRange);
    const [ydStartingVoltageRange, setYdStartingVoltageRange] = useState<Range>(defaultStartingVoltageRange);   
    const startingVoltageRange = isStartingTypeDol ? dolStartingVoltageRange : ydStartingVoltageRange;
    const { changeRange } = useHook(x => x.loadData)();
    const { getCalculationData } = useHook(x => x.filter)();

    useEffect(() => {
        const { mlfb, ac, aw } = mesRequest.commonData;

        setDolStartingVoltageRange({ ...defaultStartingVoltageRange, min: getStartingVoltageMin(mlfb, true) });
        setYdStartingVoltageRange({ ...defaultStartingVoltageRange, min: getStartingVoltageMin(mlfb, false) });
        dispatch(setStartingVoltageText(mapToSliderInputText(isStartingTypeDol ? dolStartingVoltageRange.max : ydStartingVoltageRange.max)));
        dispatch(setAcText(ac ? ac.toString() : ''));
        dispatch(setAwText(aw ? aw.toString() : ''));
    }, []);

    useEffect(() => {
        const { startingVoltage } = mesRequest.commonData;
        const { min } = startingVoltageRange;

        const newStartingVoltage = isNullOrUndefined(startingVoltage) ? min : startingVoltage;

        dispatch(setStartingVoltageText(mapToSliderInputText(Number(newStartingVoltage))));
    }, [updateFilters]);

    const updateN1SliderInputRange = (mesRequest: MesRequest) => {
        let newMesRequest = mesRequest;
        const { commonData } = newMesRequest;
        const { nN, nsyn } = newMesRequest.commonData;
        const { loadType, n1 } = newMesRequest.loadData;
        const nw = getNw(commonData);
        const newN1Range = calculateN1Range(loadType, Number(nsyn), Number(nw), Number(nN));

        changeRange(newN1Range, 'n1');

        if (newN1Range.max < Number(n1)) {
            newMesRequest = { ...newMesRequest, loadData: { ...newMesRequest.loadData, n1: newN1Range.max } };
            dispatch(setMesRequest(newMesRequest));

            const { ma, mn, nN } = newMesRequest.commonData;
            const { n4, m0, m1, m4 } = newMesRequest.loadData;

            if (Number(n4) < Number(nN)) {
                const newM4Range = calculateM4Range(loadType, Number(ma), Number(mn), Number(m0), Number(m1), Number(nN), newN1Range.max, Number(n4), Number(nsyn));

                changeRange(newM4Range, 'm4');

                if (newM4Range.max < Number(m4)) {
                    newMesRequest = { ...newMesRequest, loadData: { ...newMesRequest.loadData, m4: newM4Range.max } };

                    dispatch(setMesRequest(newMesRequest));
                }
            }
        }

        return newMesRequest;
    };

    const handleStartingConstraintChange = async (filter: Partial<CommonData>, toleranceChanged = false) => {
        dispatch(toggleLoading());

        let newMesRequest = { ...mesRequest, commonData: { ...mesRequest.commonData, ...filter } };

        dispatch(setMesRequest(newMesRequest));

        if (toleranceChanged && newMesRequest.loadData.loadType === LoadType.QuadraticWithBreakaway) {
            newMesRequest = updateN1SliderInputRange(newMesRequest);
        }

        await getCalculationData(newMesRequest);

        dispatch(toggleLoading());
    };

    return {
        startingVoltageRange,
        onStartingConstraintChange: handleStartingConstraintChange
    };
};
